import React, { useCallback, useEffect, useState } from "react";
import { Settings, Save, Play, Users, CreditCard, RefreshCw, CheckCircle2, AlertTriangle, Trash2, FileText, Sparkles, TrendingUp, Send, Zap } from "lucide-react";
import { api } from "../lib/api";
import { notify } from "../lib/notify";
import { ContentBlocksAdmin } from "../components/admin/ContentBlocksAdmin";

export default function AdminPanel() {
  const [tab, setTab] = useState("n8n");
  return (
    <div data-testid="page-admin">
      <section className="border-b border-slate-200">
        <div className="container-rp py-12">
          <div className="overline">Admin · operations</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">Control panel.</h1>
        </div>
        <div className="container-rp flex flex-wrap gap-1 border-t border-slate-100 overflow-x-auto">
          {[
            ["n8n", "n8n Automation"],
            ["engines", "Engines"],
            ["newsletter", "Newsletter"],
            ["social", "Social"],
            ["trends", "Trends"],
            ["growth", "Growth"],
            ["content", "Content"],
            ["stripe", "Stripe"],
            ["users", "Users"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-active={tab === k} className="persona-btn rounded-none border-0 border-b-2 px-4 py-3 whitespace-nowrap" style={{ borderBottomColor: tab === k ? "var(--rp-accent)" : "transparent" }} data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          {tab === "n8n" && <N8nPanel />}
          {tab === "engines" && <EnginesPanel />}
          {tab === "newsletter" && <NewsletterAdmin />}
          {tab === "social" && <SocialAdmin />}
          {tab === "trends" && <TrendsAdmin />}
          {tab === "growth" && <GrowthPanel />}
          {tab === "content" && <ContentBlocksAdmin />}
          {tab === "stripe" && <StripePanel />}
          {tab === "users" && <UsersPanel />}
        </div>
      </section>
    </div>
  );
}

function N8nPanel() {
  const [s, setS] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(async () => {
    try { const r = await api.get("/admin/n8n/runs?limit=10"); setRuns(r.data.runs); }
    catch (error) { console.error("Failed to load n8n runs:", error?.message); }
  }, []);

  useEffect(() => { api.get("/admin/n8n").then(r => setS(r.data)); loadRuns(); }, [loadRuns]);

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const update = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    try {
      await api.put("/admin/n8n", s);
      notify.success("n8n settings saved.");
    } catch { notify.error("Could not save settings."); }
  };

  const trigger = async () => {
    try {
      const r = await api.post("/admin/n8n/test-trigger");
      const fails = r.data.failures || 0;
      const total = (r.data.results || []).length;
      if (fails > 0) notify.error(`Trigger fired — ${fails}/${total} webhooks failed.`);
      else notify.success(`Trigger fired — ${total}/${total} webhooks 2xx.`);
      loadRuns();
    } catch (e) {
      notify.error(e.response?.data?.detail || "Could not trigger.");
    }
  };

  const FREQ_PRESETS = [
    { label: "Hourly", v: 60 },
    { label: "Every 6h", v: 360 },
    { label: "Daily", v: 1440 },
    { label: "Weekly", v: 10080 },
  ];

  return (
    <div className="max-w-3xl">
      <div className="overline flex items-center gap-2"><Settings size={11} /> Workflow automation</div>
      <h2 className="font-display text-3xl font-semibold mt-3">n8n circular scraping & newsletter pipeline.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Run MAS / PDPC / CSA circular scrapers via your own n8n instance. Same architecture as riskpedia.co.in — toggle below to mirror that setup.</p>

      <div className="rp-card p-6 mt-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg font-semibold">Enable n8n automation</div>
            <div className="text-xs text-slate-500 mt-1">When OFF, no webhooks are called and scheduled jobs are skipped.</div>
          </div>
          <button onClick={() => update("enabled", !s.enabled)} data-active={s.enabled} className="persona-btn" data-testid="n8n-toggle-enabled">
            {s.enabled ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div className="rp-card p-6 mt-4">
        <label className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-base font-semibold">Reuse riskpedia.co.in n8n setup</div>
            <div className="text-xs text-slate-500 mt-1">Mirror the India deployment's workflow definitions (Contabo VPS) for SG. Useful for parity.</div>
          </div>
          <input type="checkbox" checked={s.reuse_riskpedia_in_setup || false} onChange={(e) => update("reuse_riskpedia_in_setup", e.target.checked)} data-testid="n8n-reuse" />
        </label>
      </div>

      <div className="rp-card p-6 mt-4">
        <div className="overline-muted">Run frequency</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {FREQ_PRESETS.map(p => (
            <button key={p.v} onClick={() => update("frequency_minutes", p.v)} data-active={s.frequency_minutes === p.v} className="persona-btn" data-testid={`n8n-freq-${p.v}`}>{p.label}</button>
          ))}
          <input type="number" min="1" value={s.frequency_minutes || 10080} onChange={(e) => update("frequency_minutes", Number(e.target.value))} className="border border-slate-200 rounded-sm px-3 py-1.5 text-sm w-24" placeholder="min" data-testid="n8n-freq-custom" />
          <span className="text-xs text-slate-500 self-center">minutes</span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Auto-disable after consecutive failures</label>
            <input type="number" min="1" value={s.auto_disable_after_failures || 5} onChange={(e) => update("auto_disable_after_failures", Number(e.target.value))} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="n8n-auto-disable" />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Auto-enable at (UTC ISO)</label>
            <input type="datetime-local" value={(s.auto_enable_at_iso || "").slice(0, 16)} onChange={(e) => update("auto_enable_at_iso", e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="n8n-auto-enable-at" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Auto-disable at (UTC ISO)</label>
            <input type="datetime-local" value={(s.auto_disable_at_iso || "").slice(0, 16)} onChange={(e) => update("auto_disable_at_iso", e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="n8n-auto-disable-at" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="MAS scraper webhook" testid="n8n-mas" placeholder="https://n8n.example.com/webhook/mas" value={s.webhook_mas_scraper || ""} onChange={(v) => update("webhook_mas_scraper", v)} />
        <Field label="PDPC scraper webhook" testid="n8n-pdpc" placeholder="https://n8n.example.com/webhook/pdpc" value={s.webhook_pdpc_scraper || ""} onChange={(v) => update("webhook_pdpc_scraper", v)} />
        <Field label="CSA scraper webhook" testid="n8n-csa" placeholder="https://n8n.example.com/webhook/csa" value={s.webhook_csa_scraper || ""} onChange={(v) => update("webhook_csa_scraper", v)} />
        <Field label="Newsletter trigger webhook" testid="n8n-news" placeholder="https://n8n.example.com/webhook/newsletter" value={s.webhook_newsletter || ""} onChange={(v) => update("webhook_newsletter", v)} />
        <Field label="Schedule (CRON)" testid="n8n-cron" placeholder="0 6 * * 1 (Mon 6am SGT)" value={s.schedule_cron || ""} onChange={(v) => update("schedule_cron", v)} />
      </div>
      <div className="mt-6">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Notes</label>
        <textarea rows={3} value={s.notes || ""} onChange={(e) => update("notes", e.target.value)} placeholder="Operational notes…" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="n8n-notes" />
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={save} className="btn-accent" data-testid="n8n-save"><Save size={14} /> Save settings</button>
        <button onClick={trigger} className="btn-outline" disabled={!s.enabled} data-testid="n8n-trigger"><Play size={14} /> Test trigger</button>
      </div>

      {s.updated_at && <div className="text-[10px] text-slate-400 font-mono mt-6">Last updated by {s.updated_by} · {new Date(s.updated_at).toLocaleString()}</div>}

      <div className="mt-12">
        <div className="overline">Recent runs</div>
        <h3 className="font-display text-2xl font-semibold mt-3">Last {runs.length} trigger events.</h3>
        {runs.length === 0 ? <p className="text-sm text-slate-500 mt-3">No runs yet.</p> : (
          <div className="rp-card mt-6 overflow-x-auto" data-testid="n8n-runs-table">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--rp-bg-subtle)" }}>
                <tr><th className="text-left p-3">When</th><th className="text-left p-3">Source</th><th className="text-left p-3">Result</th></tr>
              </thead>
              <tbody>
                {runs.map((r, idx) => {
                  const total = Number(r.total) || 0;
                  const failures = Number(r.failures) || 0;
                  return (
                  <tr key={`${r.run_id}-${idx}`} className="border-t border-slate-100 align-top">
                    <td className="p-3 font-mono text-[11px]">{r.triggered_at ? new Date(r.triggered_at).toLocaleString() : "—"}</td>
                    <td className="p-3 text-xs">{r.source || "—"}</td>
                    <td className="p-3 text-xs">
                      <div className={failures > 0 ? "text-rose-600" : "text-emerald-600"}>{total - failures}/{total} OK</div>
                      <div className="text-slate-500 mt-1">{(r.results || []).map(x => `${(x.key || "").split('_').pop()}:${x.status || 'ERR'}`).join(" · ")}</div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid={testid} />
    </div>
  );
}

function StripePanel() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [modeOverride, setModeOverride] = useState("");
  const [notes, setNotes] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/stripe");
      setS(r.data);
      setModeOverride(r.data.mode_override || "");
      setNotes(r.data.notes || "");
    } catch {
      notify.error("Could not load Stripe settings.");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { notes };
    if (apiKey.trim()) payload.api_key = apiKey.trim();
    if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
    payload.mode_override = modeOverride || null;
    try {
      await api.put("/admin/stripe", payload);
      notify.success("Stripe settings saved.");
      setApiKey(""); setWebhookSecret("");
      await load();
    } catch (e) {
      notify.error(e.response?.data?.detail || "Could not save Stripe settings.");
    }
  };

  const clearKey = async (which) => {
    if (!window.confirm(`Remove the saved ${which === "api_key" ? "Stripe API key" : "webhook secret"} from the database? (env value, if any, will be used as fallback.)`)) return;
    try {
      const payload = which === "api_key" ? { clear_api_key: true } : { clear_webhook_secret: true };
      await api.put("/admin/stripe", payload);
      notify.success("Cleared.");
      await load();
    } catch { notify.error("Could not clear."); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/stripe/test");
      notify.success(`Stripe OK · mode=${r.data.mode} · source=${r.data.source}`);
      await load();
    } catch (e) {
      notify.error(e.response?.data?.detail || "Stripe validation failed.");
      await load();
    } finally { setTesting(false); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const modeBadgeColor = s.detected_mode === "live" ? "bg-rose-600 text-white" : s.detected_mode === "test" ? "bg-amber-500 text-white" : "bg-slate-300 text-slate-700";
  const valBadge = s.validation_status === "ok" ? { color: "text-emerald-600", Icon: CheckCircle2, label: "Validated" } : s.validation_status === "failed" ? { color: "text-rose-600", Icon: AlertTriangle, label: "Failed" } : { color: "text-slate-400", Icon: null, label: "Not validated" };

  return (
    <div className="max-w-3xl" data-testid="stripe-panel">
      <div className="overline flex items-center gap-2"><CreditCard size={11} /> Payments</div>
      <h2 className="font-display text-3xl font-semibold mt-3">Stripe configuration.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Stripe Checkout powers SGD subscription billing. Keys saved here are stored encrypted in MongoDB and take precedence over <code className="text-xs">STRIPE_API_KEY</code> in <code className="text-xs">.env</code>. Use <span className="font-mono text-xs">sk_test_…</span> while developing, switch to <span className="font-mono text-xs">sk_live_…</span> for production.</p>

      <div className="rp-card p-6 mt-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline-muted">Current state</div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${modeBadgeColor}`} data-testid="stripe-mode-badge">{(s.detected_mode || "no key").toUpperCase()}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>source: {s.api_key_source}</span>
              {s.api_key_masked ? (
                <span className="font-mono text-xs" style={{ color: "var(--rp-ink-2)" }} data-testid="stripe-key-masked">{s.api_key_masked}</span>
              ) : (
                <span className="text-xs text-rose-600">No key set</span>
              )}
            </div>
            <div className={`mt-3 flex items-center gap-1.5 text-xs ${valBadge.color}`} data-testid="stripe-validation-status">
              {valBadge.Icon && <valBadge.Icon size={12} />}
              <span>{valBadge.label}</span>
              {s.last_validated_at && <span className="text-slate-400">· {new Date(s.last_validated_at).toLocaleString()}</span>}
            </div>
            {s.validation_error && <div className="mt-2 text-xs text-rose-600 font-mono max-w-md break-words">{s.validation_error}</div>}
          </div>
          <button onClick={runTest} disabled={testing || !s.api_key_is_set} className="btn-outline" data-testid="stripe-test">
            <RefreshCw size={14} className={testing ? "animate-spin" : ""} /> {testing ? "Testing…" : "Test connection"}
          </button>
        </div>
      </div>

      <div className="rp-card p-6 mt-4 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Stripe Secret Key</label>
          <div className="flex items-center gap-2 mt-2">
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={s.api_key_is_set ? "Enter new key to replace…" : "sk_test_… or sk_live_…"} className="flex-1 border border-slate-200 rounded-sm px-3 py-2 text-sm font-mono" data-testid="stripe-key-input" autoComplete="off" />
            {s.api_key_source === "db" && (
              <button onClick={() => clearKey("api_key")} className="text-rose-600 inline-flex items-center gap-1 text-xs px-2 py-2" title="Clear stored key" data-testid="stripe-key-clear"><Trash2 size={14} /></button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">Stored once and never returned in plaintext. Get yours at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">dashboard.stripe.com/apikeys</a>.</div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Webhook Signing Secret <span className="text-slate-400 normal-case">(optional)</span></label>
          <div className="flex items-center gap-2 mt-2">
            <input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder={s.webhook_secret_is_set ? `Currently set: ${s.webhook_secret_masked}` : "whsec_…"} className="flex-1 border border-slate-200 rounded-sm px-3 py-2 text-sm font-mono" data-testid="stripe-webhook-input" autoComplete="off" />
            {s.webhook_secret_is_set && (
              <button onClick={() => clearKey("webhook_secret")} className="text-rose-600 inline-flex items-center gap-1 text-xs px-2 py-2" title="Clear stored webhook secret" data-testid="stripe-webhook-clear"><Trash2 size={14} /></button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">Used to verify webhook payloads from Stripe. Found in your Stripe webhook endpoint settings.</div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Mode override</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[["", "Auto-detect"], ["test", "Test"], ["live", "Live"]].map(([v, l]) => (
              <button key={v || "auto"} onClick={() => setModeOverride(v)} data-active={modeOverride === v} className="persona-btn" data-testid={`stripe-mode-${v || "auto"}`}>{l}</button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">Auto-detect derives the mode from the key prefix. Override only if you need to force a label.</div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Notes</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Rotation cadence, owner, etc." className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="stripe-notes" />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={save} className="btn-accent" data-testid="stripe-save"><Save size={14} /> Save Stripe settings</button>
      </div>

      {s.updated_at && <div className="text-[10px] text-slate-400 font-mono mt-6">Last updated by {s.updated_by} · {new Date(s.updated_at).toLocaleString()}</div>}

      <PackagesPanel />
    </div>
  );
}

function PackagesPanel() {
  const [pkgs, setPkgs] = useState(null);
  const [editing, setEditing] = useState(null); // pkg object being edited (with id)
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/pricing"); setPkgs(r.data.packages); }
    catch { notify.error("Could not load packages."); }
  };
  useEffect(() => { load(); }, []);

  const save = async (pkg) => {
    if (!pkg.id || !pkg.label || !(pkg.amount >= 0)) { notify.error("ID, label and amount are required."); return; }
    if (!/^[a-zA-Z0-9_-]+$/.test(pkg.id)) { notify.error("ID must be alphanumeric (underscores/hyphens allowed)."); return; }
    const { id, ...payload } = pkg;
    payload.benefits = (payload.benefits || []).filter(Boolean);
    try {
      await api.put(`/admin/pricing/${id}`, payload);
      notify.success(`Saved "${pkg.label}".`);
      setEditing(null); setAdding(false);
      await load();
    } catch (e) { notify.error(e.response?.data?.detail || "Could not save."); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.label}"? (If anyone has purchased it the package will be soft-deleted instead.)`)) return;
    try {
      const r = await api.delete(`/admin/pricing/${pkg.id}`);
      notify.success(r.data.soft_deleted ? `Soft-deleted (${r.data.ref_count} past purchases).` : "Deleted.");
      await load();
    } catch (e) { notify.error(e.response?.data?.detail || "Could not delete."); }
  };

  const toggleActive = async (pkg) => {
    try {
      const { id, ...payload } = { ...pkg, active: !pkg.active };
      await api.put(`/admin/pricing/${id}`, payload);
      await load();
    } catch { notify.error("Could not update."); }
  };

  if (!pkgs) return <div className="text-slate-400 mt-12">Loading packages…</div>;

  return (
    <div className="mt-16" data-testid="packages-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="overline">Pricing</div>
          <h3 className="font-display text-2xl font-semibold mt-2">Subscription packages.</h3>
          <p className="text-sm text-slate-500 mt-1">These are the tiers that appear on the public <code className="text-xs">/pricing</code> page and are passed to Stripe Checkout.</p>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="btn-accent" data-testid="pkg-add"><CreditCard size={14} /> Add package</button>}
      </div>

      {adding && (
        <div className="mb-4"><PackageEditor pkg={{ id: "", label: "", amount: 0, currency: "sgd", interval: "monthly", benefits: [""], active: true, sort_order: 99 }} onSave={save} onCancel={() => setAdding(false)} isNew /></div>
      )}

      <div className="space-y-3">
        {pkgs.map(pkg => (
          editing === pkg.id ? (
            <PackageEditor key={pkg.id} pkg={pkg} onSave={save} onCancel={() => setEditing(null)} />
          ) : (
            <div key={pkg.id} className="rp-card p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`pkg-row-${pkg.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>{pkg.id}</span>
                  {!pkg.active && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-300 text-slate-700">INACTIVE</span>}
                </div>
                <div className="font-display text-base font-semibold mt-2">{pkg.label}</div>
                <div className="text-xs text-slate-500 mt-1">{pkg.currency?.toUpperCase()} {pkg.amount.toFixed(2)} · {pkg.interval} · {(pkg.benefits || []).length} benefits</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => toggleActive(pkg)} className="persona-btn text-xs" data-testid={`pkg-toggle-${pkg.id}`}>{pkg.active ? "Deactivate" : "Activate"}</button>
                <button onClick={() => setEditing(pkg.id)} className="btn-outline text-xs" data-testid={`pkg-edit-${pkg.id}`}>Edit</button>
                <button onClick={() => remove(pkg)} className="text-rose-600 inline-flex items-center gap-1 text-xs px-2 py-2" data-testid={`pkg-delete-${pkg.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function PackageEditor({ pkg, onSave, onCancel, isNew = false }) {
  const [draft, setDraft] = useState({ ...pkg, benefits: pkg.benefits?.length ? [...pkg.benefits] : [""] });
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setBenefit = (i, v) => setDraft(p => ({ ...p, benefits: p.benefits.map((b, idx) => idx === i ? v : b) }));
  const addBenefit = () => setDraft(p => ({ ...p, benefits: [...p.benefits, ""] }));
  const removeBenefit = (i) => setDraft(p => ({ ...p, benefits: p.benefits.filter((_, idx) => idx !== i) }));

  return (
    <div className="rp-card p-6" data-testid={`pkg-editor-${draft.id || "new"}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={isNew ? "" : "opacity-60 pointer-events-none"}>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">ID {isNew && <span className="text-rose-600">*</span>}</label>
          <input value={draft.id} onChange={(e) => set("id", e.target.value)} placeholder="pro_monthly" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" disabled={!isNew} data-testid="pkg-field-id" />
          {isNew && <div className="text-[10px] text-slate-500 mt-1">Lowercase, underscores or hyphens. Cannot be changed later.</div>}
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Label</label>
          <input value={draft.label} onChange={(e) => set("label", e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="pkg-field-label" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Amount</label>
          <input type="number" min="0" step="0.01" value={draft.amount} onChange={(e) => set("amount", Number(e.target.value))} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="pkg-field-amount" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Currency</label>
          <select value={draft.currency} onChange={(e) => set("currency", e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="pkg-field-currency">
            {["sgd", "usd", "eur", "gbp", "inr", "myr", "idr", "hkd"].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Interval</label>
          <select value={draft.interval} onChange={(e) => set("interval", e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="pkg-field-interval">
            {["monthly", "annual", "one-time"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Sort order</label>
          <input type="number" value={draft.sort_order} onChange={(e) => set("sort_order", Number(e.target.value))} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="pkg-field-sort" />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!draft.active} onChange={(e) => set("active", e.target.checked)} data-testid="pkg-field-active" />
            <span>Active (visible on /pricing)</span>
          </label>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Benefits</label>
        <div className="space-y-2 mt-2">
          {draft.benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={b} onChange={(e) => setBenefit(i, e.target.value)} placeholder={`Benefit ${i + 1}`} className="flex-1 border border-slate-200 rounded-sm px-3 py-2 text-sm" data-testid={`pkg-benefit-${i}`} />
              <button onClick={() => removeBenefit(i)} className="text-rose-600 px-2" data-testid={`pkg-benefit-rm-${i}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={addBenefit} className="text-xs text-slate-500 hover:text-slate-900 mt-2" data-testid="pkg-benefit-add">+ Add benefit</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => onSave(draft)} className="btn-accent" data-testid="pkg-save"><Save size={14} /> {isNew ? "Add package" : "Save changes"}</button>
        <button onClick={onCancel} className="btn-outline" data-testid="pkg-cancel">Cancel</button>
      </div>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data.users)); }, []);
  return (
    <div>
      <div className="overline flex items-center gap-2"><Users size={11} /> Members</div>
      <h2 className="font-display text-3xl font-semibold mt-3">{users.length} accounts.</h2>
      <div className="rp-card mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead style={{ background: "var(--rp-bg-subtle)" }}>
            <tr>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Pro</th>
              <th className="text-left p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3"><span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>{u.role}</span></td>
                <td className="p-3">{u.is_pro ? <span className="text-emerald-600 text-xs font-medium">✓ Pro</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                <td className="p-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function EnginesPanel() {
  const [eng, setEng] = useState(null);
  const load = () => api.get("/admin/engines").then(r => setEng(r.data));
  useEffect(() => { load(); }, []);
  const update = async (pipeline, engine) => {
    try { await api.put("/admin/engines", { pipeline, engine }); notify.success(`${pipeline} → ${engine}`); load(); }
    catch { notify.error("Could not update."); }
  };
  if (!eng) return <div className="text-slate-400">Loading…</div>;
  const PIPELINES = [
    ["newsletter", "Newsletter generation", "Generate the weekly Singapore Risk Briefing."],
    ["mas_scraper", "MAS circular scraper", "Fetch and summarise the latest MAS notices."],
    ["pdpc_scraper", "PDPC scraper", "PDPC enforcement decisions and advisories."],
    ["csa_scraper", "CSA scraper", "Cyber Security Agency advisories and CCoP updates."],
  ];
  return (
    <div className="max-w-3xl" data-testid="engines-panel">
      <div className="overline flex items-center gap-2"><Zap size={11} /> Engine selector</div>
      <h2 className="font-display text-3xl font-semibold mt-3">n8n or Claude — your call.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">For each pipeline, choose the engine. <strong>n8n</strong> connects to your external workflow service via the webhook URLs configured in the n8n tab. <strong>Claude</strong> runs in-app — no external setup, content generated by Claude Sonnet 4.5 via the Emergent LLM key.</p>
      <div className="space-y-3 mt-8">
        {PIPELINES.map(([key, label, desc]) => (
          <div key={key} className="rp-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`engine-${key}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-base font-semibold">{label}</div>
              <div className="text-xs text-slate-500 mt-1">{desc}</div>
            </div>
            <div className="flex gap-2">
              {["n8n", "claude"].map(e => (
                <button key={e} onClick={() => update(key, e)} data-active={eng[key] === e} className="persona-btn text-xs uppercase" data-testid={`engine-${key}-${e}`}>{e}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsletterAdmin() {
  const [bullets, setBullets] = useState("");
  const [extra, setExtra] = useState("");
  const [draft, setDraft] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState([]);

  const loadIssues = () => api.get("/admin/newsletter?limit=10").then(r => setIssues(r.data.issues || []));
  useEffect(() => { loadIssues(); }, []);

  const generate = async () => {
    setBusy(true); setDraft(null);
    try {
      const bullList = bullets.split("\n").map(b => b.trim()).filter(Boolean);
      const r = await api.post("/admin/newsletter/generate", { bullets: bullList, extra_instructions: extra });
      setDraft(r.data);
      // Auto-extract title from first H1
      const m = r.data.markdown?.match(/^#\s+(.+)$/m);
      setTitle(m ? m[1] : `Issue ${r.data.issue_number}`);
      notify.success("Draft generated by Claude.");
    } catch (e) { notify.error(e.response?.data?.detail || "Generation failed."); }
    finally { setBusy(false); }
  };

  const save = async (publish) => {
    try {
      await api.post("/admin/newsletter", { title, markdown: draft.markdown, publish });
      notify.success(publish ? "Published." : "Saved as draft.");
      setDraft(null); setTitle(""); setBullets(""); setExtra("");
      loadIssues();
    } catch { notify.error("Could not save."); }
  };

  return (
    <div className="max-w-3xl" data-testid="newsletter-panel">
      <div className="overline flex items-center gap-2"><FileText size={11} /> Newsletter</div>
      <h2 className="font-display text-3xl font-semibold mt-3">Singapore Risk Briefing — Claude editor.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Paste this week's seed bullets (any rough notes — MAS announcements, PDPC fines, breaches you spotted). Claude will draft a 4-minute issue enriched with the top Google Trends keywords for SG. Review, edit the title, then publish.</p>

      <div className="rp-card p-6 mt-8">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Seed bullets (one per line)</label>
        <textarea rows={6} value={bullets} onChange={(e) => setBullets(e.target.value)} placeholder="MAS released new TRM notice on AI governance&#10;PDPC fined Acme Pte S$50,000 for misdirected email&#10;CSA issued ransomware advisory" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="newsletter-bullets" />
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono mt-4 block">Extra instructions (optional)</label>
        <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. 'lead with the AI story', 'angle for sales/BD persona'" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="newsletter-extra" />
        <div className="mt-5 flex gap-3">
          <button onClick={generate} disabled={busy} className="btn-accent" data-testid="newsletter-generate"><Sparkles size={14} /> {busy ? "Generating with Claude…" : "Generate draft"}</button>
        </div>
      </div>

      {draft && (
        <div className="rp-card p-6 mt-6" data-testid="newsletter-draft">
          <div className="overline flex items-center gap-2"><Sparkles size={11} /> Claude draft · Issue {draft.issue_number}</div>
          {draft.trends_used?.length > 0 && (
            <div className="text-[11px] font-mono text-slate-500 mt-2">Trends used: {draft.trends_used.join(" · ")}</div>
          )}
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono mt-4 block">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-display text-lg" data-testid="newsletter-title" />
          <label className="text-xs uppercase tracking-widest text-slate-500 font-mono mt-4 block">Markdown body</label>
          <textarea rows={18} value={draft.markdown} onChange={(e) => setDraft({ ...draft, markdown: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-xs mt-2 font-mono" data-testid="newsletter-md" />
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => save(true)} className="btn-accent" data-testid="newsletter-publish"><Send size={14} /> Publish to /newsletter</button>
            <button onClick={() => save(false)} className="btn-outline" data-testid="newsletter-savedraft"><Save size={14} /> Save as draft</button>
            <button onClick={() => setDraft(null)} className="text-xs text-slate-400 underline">Discard</button>
          </div>
        </div>
      )}

      <div className="mt-12">
        <div className="overline">Recent issues · {issues.length}</div>
        <div className="rp-card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--rp-bg-subtle)" }}>
              <tr><th className="text-left p-3">#</th><th className="text-left p-3">Title</th><th className="text-left p-3">Status</th><th className="text-left p-3">Created</th></tr>
            </thead>
            <tbody>
              {issues.map(it => (
                <tr key={it.issue_id} className="border-t border-slate-100">
                  <td className="p-3 font-mono text-xs">{it.issue_number}</td>
                  <td className="p-3">{it.title}</td>
                  <td className="p-3">{it.published ? <span className="text-emerald-600 text-xs">Published</span> : <span className="text-slate-400 text-xs">Draft</span>}</td>
                  <td className="p-3 text-xs text-slate-500">{new Date(it.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SocialAdmin() {
  const [src, setSrc] = useState("");
  const [link, setLink] = useState("");
  const [posts, setPosts] = useState(null);
  const [busy, setBusy] = useState(false);

  const compose = async () => {
    if (!src.trim()) { notify.error("Source text required."); return; }
    setBusy(true); setPosts(null);
    try {
      const r = await api.post("/admin/social/compose", { source_text: src, link });
      setPosts(r.data.posts);
      notify.success("3 posts composed by Claude.");
    } catch (e) { notify.error(e.response?.data?.detail || "Compose failed."); }
    finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text).then(() => notify.success("Copied.")); };

  return (
    <div className="max-w-3xl" data-testid="social-panel">
      <div className="overline flex items-center gap-2"><Send size={11} /> Social composer</div>
      <h2 className="font-display text-3xl font-semibold mt-3">LinkedIn · X · Telegram — in one click.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Paste a framework summary, tool result, newsletter paragraph or any RiskPedia content. Claude rewrites it into 3 platform-specific posts with SG-relevant hashtags.</p>

      <div className="rp-card p-6 mt-8">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Source material</label>
        <textarea rows={6} value={src} onChange={(e) => setSrc(e.target.value)} placeholder="Paste a framework description, wizard result, newsletter excerpt…" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="social-src" />
        <label className="text-xs uppercase tracking-widest text-slate-500 font-mono mt-4 block">Link to use as {`{link}`}</label>
        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://riskpedia.sg/workflows" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="social-link" />
        <div className="mt-5">
          <button onClick={compose} disabled={busy} className="btn-accent" data-testid="social-compose"><Sparkles size={14} /> {busy ? "Composing…" : "Compose 3 posts"}</button>
        </div>
      </div>

      {posts && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" data-testid="social-output">
          {[["LinkedIn", posts.linkedin], ["X / Twitter", posts.twitter], ["Telegram", posts.telegram]].map(([label, text]) => (
            <div key={label} className="rp-card p-5">
              <div className="overline-muted">{label}</div>
              <div className="text-xs text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed" style={{ color: "var(--rp-ink-1)" }}>{text}</div>
              <button onClick={() => copy(text)} className="btn-outline text-xs mt-4" data-testid={`social-copy-${label.toLowerCase().split(" ")[0]}`}>Copy</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrendsAdmin() {
  const [trends, setTrends] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/trends/sg").then(r => setTrends(r.data));
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/trends/refresh"); setTrends(r.data); notify.success(`Refreshed · ${r.data.trends?.length} keywords (source: ${r.data.source}).`); }
    catch { notify.error("Refresh failed."); }
    finally { setBusy(false); }
  };
  if (!trends) return <div className="text-slate-400">Loading…</div>;
  return (
    <div className="max-w-3xl" data-testid="trends-panel">
      <div className="overline flex items-center gap-2"><TrendingUp size={11} /> Google Trends · Singapore</div>
      <h2 className="font-display text-3xl font-semibold mt-3">SG search interest cache.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Live <code className="text-xs">pytrends</code> data for 15 Singapore risk/compliance keywords. Cached for 12 hours. Refresh manually here, or let the cache miss trigger it on first read.</p>
      <div className="rp-card p-6 mt-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="overline-muted">Status</div>
          <div className="mt-2 flex items-center gap-2">
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${trends.source === "google_trends_sg" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>{trends.source}</span>
            <span className="text-xs text-slate-500">cached {new Date(trends.cached_at).toLocaleString()}</span>
          </div>
        </div>
        <button onClick={refresh} disabled={busy} className="btn-outline" data-testid="trends-refresh"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh now</button>
      </div>
      <div className="mt-6 rp-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="trends-table">
          <thead style={{ background: "var(--rp-bg-subtle)" }}>
            <tr><th className="text-left p-3">#</th><th className="text-left p-3">Keyword</th><th className="text-right p-3">Latest score</th><th className="text-left p-3">Rising queries</th></tr>
          </thead>
          <tbody>
            {(trends.trends || []).map((t, i) => (
              <tr key={t.keyword} className="border-t border-slate-100 align-top">
                <td className="p-3 font-mono text-xs">{i + 1}</td>
                <td className="p-3 font-medium">{t.keyword}</td>
                <td className="p-3 text-right font-mono">{t.latest_score}</td>
                <td className="p-3 text-xs text-slate-500">{(trends.related_rising?.[t.keyword] || []).slice(0, 3).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrowthPanel() {
  const [sub, setSub] = useState("frameworks");
  return (
    <div data-testid="growth-panel">
      <div className="overline">Growth ops</div>
      <h2 className="font-display text-3xl font-semibold mt-3">Content · Coupons · Bots · Leads.</h2>
      <div className="flex flex-wrap gap-1 mt-6 border-b border-slate-200">
        {[
          ["frameworks", "Frameworks"],
          ["tools", "Tools"],
          ["coupons", "Coupons"],
          ["bots", "Bots"],
          ["leads", "Enterprise leads"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-active={sub === k} className="persona-btn rounded-none border-0 border-b-2 px-4 py-2 text-xs" style={{ borderBottomColor: sub === k ? "var(--rp-accent)" : "transparent" }} data-testid={`growth-sub-${k}`}>{l}</button>
        ))}
      </div>
      <div className="mt-8">
        {sub === "frameworks" && <FrameworksAdmin />}
        {sub === "tools" && <ToolsAdmin />}
        {sub === "coupons" && <CouponsAdmin />}
        {sub === "bots" && <BotsAdmin />}
        {sub === "leads" && <LeadsAdmin />}
      </div>
    </div>
  );
}

function FrameworksAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/frameworks").then(r => setItems(r.data.frameworks || []));
  useEffect(() => { load(); }, []);
  const remove = async (slug) => { if (!window.confirm(`Hide framework "${slug}"?`)) return; try { await api.delete(`/admin/frameworks/${slug}`); notify.success("Removed."); load(); } catch { notify.error("Failed."); } };

  return (
    <div data-testid="frameworks-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">{items.length} frameworks · seed-backed with admin overlay</div>
        {!showNew && <button onClick={() => { setShowNew(true); setEditing({ slug: "" }); }} className="btn-accent text-xs" data-testid="fw-add">+ Add framework</button>}
      </div>
      {editing && <FrameworkEditor pkg={editing} isNew={showNew} onCancel={() => { setEditing(null); setShowNew(false); }} onSaved={() => { setEditing(null); setShowNew(false); load(); }} />}
      <div className="rp-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--rp-bg-subtle)" }}><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Domain</th><th className="text-left p-3">Region</th><th className="text-right p-3">SG rel</th><th className="text-right p-3"></th></tr></thead>
          <tbody>
            {items.map(f => (
              <tr key={f.slug} className="border-t border-slate-100" data-testid={`fw-row-${f.slug}`}>
                <td className="p-3"><div className="font-medium">{f.name}</div><div className="text-[10px] font-mono text-slate-400">{f.slug}</div></td>
                <td className="p-3 text-xs">{f.domain}</td>
                <td className="p-3 text-xs">{f.region}</td>
                <td className="p-3 text-right font-mono text-xs">{f.sg_relevance || 50}</td>
                <td className="p-3 text-right"><button onClick={() => setEditing(f)} className="btn-outline text-xs mr-2" data-testid={`fw-edit-${f.slug}`}>Edit</button><button onClick={() => remove(f.slug)} className="text-rose-600 text-xs"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FrameworkEditor({ pkg, isNew, onCancel, onSaved }) {
  const [d, setD] = useState({ slug: pkg.slug || "", name: pkg.name || "", domain: pkg.domain || "Operational risk", description: pkg.description || "", region: pkg.region || "Singapore", type: pkg.type || "Framework", complexity: pkg.complexity || "Medium", certification: !!pkg.certification, time_to_implement: pkg.time_to_implement || "6-12 months", sg_relevance: pkg.sg_relevance ?? 50, pillars: pkg.pillars || [], active: pkg.active !== false });
  const save = async () => {
    if (!d.slug || !d.name) { notify.error("Slug + name required."); return; }
    const { slug, ...payload } = d;
    try { await api.put(`/admin/frameworks/${slug}`, payload); notify.success(`Saved "${d.name}".`); onSaved(); }
    catch (e) { notify.error(e.response?.data?.detail || "Save failed."); }
  };
  return (
    <div className="rp-card p-6" data-testid="fw-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={`Slug${isNew ? " *" : ""}`} testid="fw-slug" value={d.slug} onChange={(v) => isNew && setD({ ...d, slug: v })} placeholder="mas-trm-guidelines" />
        <Field label="Name" testid="fw-name" value={d.name} onChange={(v) => setD({ ...d, name: v })} />
        <Field label="Domain" testid="fw-domain" value={d.domain} onChange={(v) => setD({ ...d, domain: v })} />
        <Field label="Region" testid="fw-region" value={d.region} onChange={(v) => setD({ ...d, region: v })} />
        <Field label="Complexity" testid="fw-complex" value={d.complexity} onChange={(v) => setD({ ...d, complexity: v })} />
        <Field label="Time to implement" testid="fw-time" value={d.time_to_implement} onChange={(v) => setD({ ...d, time_to_implement: v })} />
        <div><label className="text-xs uppercase tracking-widest text-slate-500 font-mono">SG relevance (0-100)</label><input type="number" min="0" max="100" value={d.sg_relevance} onChange={(e) => setD({ ...d, sg_relevance: Number(e.target.value) })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="fw-sgrel" /></div>
        <label className="inline-flex items-center gap-2 text-sm pt-7"><input type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} data-testid="fw-active" /> Active</label>
      </div>
      <div className="mt-4"><label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Description</label><textarea rows={4} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="fw-desc" /></div>
      <div className="mt-5 flex gap-3"><button onClick={save} className="btn-accent" data-testid="fw-save"><Save size={14} /> Save</button><button onClick={onCancel} className="btn-outline">Cancel</button></div>
    </div>
  );
}

function ToolsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/tools").then(r => setItems(r.data.tools || []));
  useEffect(() => { load(); }, []);
  const remove = async (slug) => { if (!window.confirm(`Hide tool "${slug}"?`)) return; try { await api.delete(`/admin/tools/${slug}`); notify.success("Removed."); load(); } catch { notify.error("Failed."); } };
  return (
    <div data-testid="tools-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">{items.length} tools · seed + overlay</div>
        {!showNew && <button onClick={() => { setShowNew(true); setEditing({ slug: "" }); }} className="btn-accent text-xs" data-testid="tool-add">+ Add tool</button>}
      </div>
      {editing && (
        <div className="rp-card p-6" data-testid="tool-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={`Slug${showNew ? " *" : ""}`} testid="tool-slug" value={editing.slug} onChange={(v) => showNew && setEditing({ ...editing, slug: v })} />
            <Field label="Name" testid="tool-name" value={editing.name || ""} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field label="Category" testid="tool-cat" value={editing.category || "Assessor"} onChange={(v) => setEditing({ ...editing, category: v })} />
            <div><label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Duration (min)</label><input type="number" value={editing.duration_minutes || 5} onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="tool-dur" /></div>
          </div>
          <div className="mt-4"><label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Description</label><textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="tool-desc" /></div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="tool-active" /> Active</label>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_pro} onChange={(e) => setEditing({ ...editing, is_pro: e.target.checked })} data-testid="tool-pro" /> Pro-only</label>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={async () => { if (!editing.slug || !editing.name) { notify.error("Slug+name required."); return; } const { slug, ...payload } = editing; try { await api.put(`/admin/tools/${slug}`, payload); notify.success("Saved."); setEditing(null); setShowNew(false); load(); } catch { notify.error("Failed."); } }} className="btn-accent" data-testid="tool-save"><Save size={14} /> Save</button>
            <button onClick={() => { setEditing(null); setShowNew(false); }} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}
      <div className="rp-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--rp-bg-subtle)" }}><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Category</th><th className="text-right p-3">Min</th><th className="text-right p-3"></th></tr></thead>
          <tbody>
            {items.map(t => (
              <tr key={t.slug} className="border-t border-slate-100" data-testid={`tool-row-${t.slug}`}>
                <td className="p-3"><div className="font-medium">{t.name}</div><div className="text-[10px] font-mono text-slate-400">{t.slug}</div></td>
                <td className="p-3 text-xs">{t.category}</td>
                <td className="p-3 text-right font-mono text-xs">{t.duration_minutes || 5}</td>
                <td className="p-3 text-right"><button onClick={() => setEditing(t)} className="btn-outline text-xs mr-2" data-testid={`tool-edit-${t.slug}`}>Edit</button><button onClick={() => remove(t.slug)} className="text-rose-600 text-xs"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CouponsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: "", amount_off_sgd: "", max_redemptions: "", expires_at_iso: "", active: true });
  const load = () => api.get("/admin/coupons").then(r => setItems(r.data.coupons || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.code) { notify.error("Code required."); return; }
    const payload = { code: form.code, active: form.active };
    if (form.percent_off) payload.percent_off = Number(form.percent_off);
    else if (form.amount_off_sgd) payload.amount_off_sgd = Number(form.amount_off_sgd);
    if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
    if (form.expires_at_iso) payload.expires_at_iso = form.expires_at_iso;
    try { await api.put(`/admin/coupons/${form.code}`, payload); notify.success("Saved."); setForm({ code: "", percent_off: "", amount_off_sgd: "", max_redemptions: "", expires_at_iso: "", active: true }); load(); }
    catch (e) { notify.error(e.response?.data?.detail || "Save failed."); }
  };
  const del = async (c) => { if (!window.confirm(`Delete coupon ${c}?`)) return; try { await api.delete(`/admin/coupons/${c}`); notify.success("Deleted."); load(); } catch { notify.error("Failed."); } };
  return (
    <div data-testid="coupons-admin">
      <div className="rp-card p-6">
        <div className="overline-muted">Create / edit coupon</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Field label="Code" testid="cpn-code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="LAUNCH50" />
          <Field label="Percent off" testid="cpn-pct" value={form.percent_off} onChange={(v) => setForm({ ...form, percent_off: v, amount_off_sgd: "" })} placeholder="50" />
          <Field label="Or fixed S$ off" testid="cpn-amt" value={form.amount_off_sgd} onChange={(v) => setForm({ ...form, amount_off_sgd: v, percent_off: "" })} placeholder="10" />
          <Field label="Max redemptions" testid="cpn-max" value={form.max_redemptions} onChange={(v) => setForm({ ...form, max_redemptions: v })} placeholder="100" />
          <Field label="Expires (ISO, optional)" testid="cpn-exp" value={form.expires_at_iso} onChange={(v) => setForm({ ...form, expires_at_iso: v })} placeholder="2026-12-31T23:59:59+08:00" />
          <label className="inline-flex items-center gap-2 text-sm pt-7"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="cpn-active" /> Active</label>
        </div>
        <button onClick={save} className="btn-accent mt-5" data-testid="cpn-save"><Save size={14} /> Save coupon</button>
      </div>
      <div className="rp-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--rp-bg-subtle)" }}><tr><th className="text-left p-3">Code</th><th className="text-left p-3">Discount</th><th className="text-right p-3">Redemptions</th><th className="text-left p-3">Active</th><th className="text-right p-3"></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.code} className="border-t border-slate-100" data-testid={`cpn-row-${c.code}`}>
                <td className="p-3 font-mono">{c.code}</td>
                <td className="p-3 text-xs">{c.percent_off ? `${c.percent_off}%` : `S$${c.amount_off_sgd}`}{c.max_redemptions ? ` · max ${c.max_redemptions}` : ""}</td>
                <td className="p-3 text-right font-mono text-xs">{c.redemptions || 0}</td>
                <td className="p-3 text-xs">{c.active ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Off</span>}</td>
                <td className="p-3 text-right"><button onClick={() => del(c.code)} className="text-rose-600 text-xs"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BotsAdmin() {
  const [b, setB] = useState(null);
  const [form, setForm] = useState({ slack_webhook_url: "", telegram_webhook_url: "", whatsapp_webhook_url: "", teams_webhook_url: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/bots").then(r => setB(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = {};
    if (form.slack_webhook_url) payload.slack_webhook_url = form.slack_webhook_url;
    if (form.telegram_webhook_url) payload.telegram_webhook_url = form.telegram_webhook_url;
    if (form.whatsapp_webhook_url) payload.whatsapp_webhook_url = form.whatsapp_webhook_url;
    if (form.teams_webhook_url) payload.teams_webhook_url = form.teams_webhook_url;
    if (Object.keys(payload).length === 0) { notify.error("Nothing to save."); return; }
    try { await api.put("/admin/bots", payload); notify.success("Bots saved."); setForm({ slack_webhook_url: "", telegram_webhook_url: "", whatsapp_webhook_url: "", teams_webhook_url: "" }); load(); } catch { notify.error("Save failed."); }
  };
  const clearOne = async (k) => { if (!window.confirm(`Clear ${k} webhook?`)) return; try { await api.put("/admin/bots", { clear: k }); notify.success("Cleared."); load(); } catch { notify.error("Failed."); } };
  const test = async () => { setBusy(true); try { const r = await api.post("/admin/bots/test"); notify.success(`Test: ${Object.entries(r.data.results).map(([k, v]) => `${k}=${v}`).join(" · ")}`); } catch { notify.error("Test failed."); } finally { setBusy(false); } };
  if (!b) return <div className="text-slate-400">Loading…</div>;
  return (
    <div data-testid="bots-admin">
      <div className="rp-card p-6">
        <div className="overline flex items-center gap-2"><Sparkles size={11} /> Workflow result fan-out</div>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">Each time a user runs a workflow, the Claude-summarised result is POSTed to every webhook configured here. Standard incoming-webhook format (JSON {`{text: "..."}`}). Tested against Slack, MS Teams; for Telegram/WhatsApp use a relay service (e.g. WhatsApp Cloud API or a Telegram bot webhook).</p>
        <div className="mt-6 space-y-4">
          {[["slack", "Slack incoming webhook"], ["telegram", "Telegram bot webhook"], ["whatsapp", "WhatsApp relay webhook"], ["teams", "MS Teams webhook"]].map(([k, l]) => (
            <div key={k} className="border border-slate-200 rounded-sm p-3" data-testid={`bot-${k}`}>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">{l}</label>
              <div className="flex items-center gap-2 mt-2">
                <input value={form[`${k}_webhook_url`]} onChange={(e) => setForm({ ...form, [`${k}_webhook_url`]: e.target.value })} placeholder={b[k]?.set ? `Currently set: ${b[k].masked}` : "https://hooks.slack.com/services/…"} className="flex-1 border border-slate-200 rounded-sm px-3 py-2 text-xs font-mono" data-testid={`bot-${k}-input`} />
                {b[k]?.set && <button onClick={() => clearOne(k)} className="text-rose-600 text-xs"><Trash2 size={12} /></button>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} className="btn-accent" data-testid="bot-save"><Save size={14} /> Save</button>
          <button onClick={test} disabled={busy} className="btn-outline" data-testid="bot-test">{busy ? "Testing…" : "Send test message"}</button>
        </div>
      </div>
    </div>
  );
}

function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/admin/enterprise/leads").then(r => setLeads(r.data.leads || [])).catch(() => {}); }, []);
  return (
    <div data-testid="leads-admin">
      <div className="rp-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--rp-bg-subtle)" }}><tr><th className="text-left p-3">When</th><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Company</th><th className="text-left p-3">Role</th><th className="text-right p-3">Seats</th></tr></thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400 text-sm">No enterprise leads yet.</td></tr>}
            {leads.map(l => (
              <tr key={l.lead_id} className="border-t border-slate-100 align-top" data-testid={`lead-${l.lead_id}`}>
                <td className="p-3 text-xs font-mono text-slate-500">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="p-3 font-medium">{l.name}</td>
                <td className="p-3 text-xs"><a href={`mailto:${l.work_email}`} className="underline">{l.work_email}</a></td>
                <td className="p-3 text-xs">{l.company}</td>
                <td className="p-3 text-xs">{l.role}</td>
                <td className="p-3 text-right font-mono text-xs">{l.seats}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

